import { type ApiErrorResponse } from '@erp/shared';
import axios, { type AxiosError } from 'axios';

import { APP_CONFIG } from '../config';
import { useAuthStore } from '../shared/auth/authStore';

/** Axios client duy nhất: xác thực bằng cookie HttpOnly và gửi công ty đang chọn. */
export const apiClient = axios.create({
  baseURL: `${APP_CONFIG.apiBaseUrl}${APP_CONFIG.apiPrefix}`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

function cookieValue(name: string): string | undefined {
  const prefix = `${encodeURIComponent(name)}=`;
  const item = document.cookie.split('; ').find((part) => part.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : undefined;
}

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const companyId = useAuthStore.getState().activeCompanyId;
  if (companyId) {
    config.headers['x-company-id'] = companyId;
  }
  if (config.method && ['post', 'put', 'patch', 'delete'].includes(config.method.toLowerCase())) {
    config.headers['Idempotency-Key'] ??= crypto.randomUUID();
    const csrfToken = cookieValue('erp_csrf');
    if (csrfToken) config.headers['x-csrf-token'] = csrfToken;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    if (error.response?.status === 401) {
      useAuthStore.getState().logout();
    }

    return Promise.reject(error);
  },
);

export function getApiErrorMessage(error: unknown): string {
  const err = error as AxiosError<ApiErrorResponse>;
  return err.response?.data?.error?.message ?? err.message ?? 'Đã có lỗi xảy ra';
}

export function getApiErrorCode(error: unknown): string | undefined {
  const err = error as AxiosError<ApiErrorResponse>;
  return err.response?.data?.error?.code;
}

export function isUnauthorized(error: unknown): boolean {
  return (error as AxiosError).response?.status === 401;
}

export function isNotFound(error: unknown): boolean {
  return (error as AxiosError).response?.status === 404;
}
